import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, AlertIcon, Badge, Box, Button, Checkbox, Divider, Flex, FormControl, FormLabel, HStack, Icon, IconButton,
    Image, Input, InputGroup, InputLeftElement, Link as ChakraLink, Modal, ModalBody, ModalCloseButton, ModalContent,
    ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid, Skeleton, Spinner, Text, Textarea, Tooltip, VStack,
    useBreakpointValue, useToast,
} from '@chakra-ui/react';
import { ExternalLinkIcon, RepeatIcon } from '@chakra-ui/icons';
import {
    FaArrowDown, FaBoxOpen, FaCheckCircle, FaClipboardCheck, FaMapMarkerAlt, FaMotorcycle, FaCarSide, FaPhone,
    FaPlus, FaSearch, FaShuttleVan, FaStore, FaTimesCircle, FaTruck, FaUnlink, FaUser, FaWallet,
} from 'react-icons/fa';
import {
    aumentarGorjetaAgoraLalamove, cancelarOrdemLalamove, cotarColetaLalamove, cotarModalidadeColetaLalamove,
    criarColetaLalamove, descartarPendenteLalamove, desvincularCompraColetaLalamove, getCorridaColetaLalamove,
    getLalamoveServicos, getPontosColetaLalamove, pausarAumentoGorjetaLalamove, prepararColetaLalamove,
    trocarMotoristaLalamove,
} from '../../services/api';
import ConfirmDialog from '../ConfirmDialog';
import EscolhaPrioridade from '../GorjetaLalamove/EscolhaPrioridade';
import PainelGorjeta from '../GorjetaLalamove/PainelGorjeta';
import { normalizar as normalizarGorjeta, valor as valorGorjeta, valorTeto as tetoGorjeta } from '../GorjetaLalamove/regras';
import SeletorEnderecoMapa from '../SeletorEnderecoMapa';
import {
    doCadastro, impedimentos as impedimentosDoEndereco, linhaLalamove, observacaoDoMotorista, paraPayload,
} from '../SeletorEnderecoMapa/endereco';
import FormEnderecoColeta from './FormEnderecoColeta';
import {
    chaveCompra, LIMITE_TEXTO, nomeRemetente, pendenciasDaSelecao, textoMotorista,
} from './texto';

const ICONE_SERVICO = {
    LALAGO: FaMotorcycle, LALAPRO: FaMotorcycle, HATCHBACK: FaCarSide, CAR: FaCarSide,
    UV_FIORINO: FaShuttleVan, VAN: FaShuttleVan, TRUCK330: FaTruck, TRUCK3_5T: FaTruck,
};

const CORES_STATUS = {
    PENDENTE: 'gray', ASSIGNING_DRIVER: 'yellow', ON_GOING: 'blue', PICKED_UP: 'purple',
    COMPLETED: 'green', CANCELED: 'red', EXPIRED: 'red', REJECTED: 'red',
};

const brl = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const dataCurta = (v) => (v ? new Date(v).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '-');
const JANELAS = [3, 7, 15, 30];
const segundosAte = (quando) => (quando ? Math.max(0, Math.floor((new Date(quando).getTime() - Date.now()) / 1000)) : 0);

/**
 * Coleta de compra no fornecedor pela Lalamove (#2307).
 *
 * Mesmo caminho do modal de venda (ModalSolicitacaoLalamove): conferir o
 * endereco, cotar todas as modalidades, adicionais, prioridade e gorjeta,
 * confirmar e acompanhar. A diferenca e a direcao (sai do fornecedor e chega
 * na loja) e o que vai na corrida: os pedidos do mesmo fornecedor que a equipe
 * marcar, cada um com o numero do pedido do fornecedor e a palavra-chave.
 *
 * Entradas:
 *  - `compra` ({tipo, id}): aberto do card da lista ou do pedido de furo;
 *  - `ordemId`: abre uma corrida de coleta ja existente (acompanhar, tirar pedido);
 *  - nenhum dos dois: corrida avulsa da pagina da Lalamove, escolhendo o fornecedor.
 */
export default function ModalColetaLalamove({ isOpen, onClose, compra = null, ordemId = null, onMudou }) {
    const toast = useToast();
    const compacto = useBreakpointValue({ base: true, md: false });

    const [etapa, setEtapa] = useState('carregando');
    const [erro, setErro] = useState(null);

    const [pontos, setPontos] = useState([]);
    const [fornecedores, setFornecedores] = useState({ listas: [], furos: [] });
    const [buscaPonto, setBuscaPonto] = useState('');
    const [semEndereco, setSemEndereco] = useState(null);

    const [prep, setPrep] = useState(null);
    const [janela, setJanela] = useState(7);
    const [selecao, setSelecao] = useState({});
    const [origem, setOrigem] = useState(() => doCadastro({}));
    const [salvarEndereco, setSalvarEndereco] = useState(false);
    const [textoEditado, setTextoEditado] = useState(null);

    const [cotacoes, setCotacoes] = useState([]);
    const [falhas, setFalhas] = useState({});
    const [escolhida, setEscolhida] = useState(null);
    const [cotando, setCotando] = useState(false);
    const [restante, setRestante] = useState(0);
    const [adicionaisPorServico, setAdicionaisPorServico] = useState({});
    const [adicionais, setAdicionais] = useState([]);
    const [recotando, setRecotando] = useState(false);
    const [totalSemAdicionais, setTotalSemAdicionais] = useState(null);
    const [prioridade, setPrioridade] = useState(false);
    const [regrasGorjeta, setRegrasGorjeta] = useState(null);
    const [confirmando, setConfirmando] = useState(false);

    const [ordem, setOrdem] = useState(null);
    const [comprasCorrida, setComprasCorrida] = useState([]);
    const [pontoCorrida, setPontoCorrida] = useState(null);
    const [confirmarNaLalamove, setConfirmarNaLalamove] = useState(false);
    const [atualizando, setAtualizando] = useState(false);
    const [pausandoGorjeta, setPausandoGorjeta] = useState(false);
    const [acao, setAcao] = useState(null); // {tipo: 'cancelar'|'desvincular'|'descartar'|'trocar', alvo}
    const [executando, setExecutando] = useState(false);
    const [motivoTroca, setMotivoTroca] = useState('DRIVER_LATE');

    const expiraEm = useRef(null);
    const corpoRef = useRef(null);

    useEffect(() => { if (corpoRef.current) corpoRef.current.scrollTop = 0; }, [etapa]);

    const avisarMudanca = useCallback(() => { if (onMudou) onMudou(); }, [onMudou]);

    // ── Carregamento ────────────────────────────────────────────────────

    const mostrarCorrida = useCallback((res) => {
        setOrdem(res.ordem);
        setComprasCorrida(res.compras || []);
        setPontoCorrida(res.ponto || null);
        setConfirmarNaLalamove(!!res.confirmar_na_lalamove || (res.ordem?.status === 'PENDENTE' && !res.ordem?.order_id));
        setEtapa('criada');
    }, []);

    const abrirPreparo = useCallback((res) => {
        if (res.ja_solicitada) {
            mostrarCorrida(res);
            return;
        }

        if (res.sem_endereco) {
            setSemEndereco(res);
            getPontosColetaLalamove()
                .then((p) => setFornecedores(p.fornecedores || { listas: [], furos: [] }))
                .catch(() => setFornecedores({ listas: [], furos: [] }));
            setEtapa('semEndereco');
            return;
        }

        setPrep(res);
        setOrigem({ ...doCadastro(res.origem || {}), pontoConfirmado: true });
        setSalvarEndereco(false);
        setTextoEditado(null);

        // O pedido do card ja vem marcado; os outros a equipe escolhe.
        setSelecao((anterior) => {
            const nova = {};
            (res.compras || []).forEach((c) => {
                const chave = chaveCompra(c);
                const ja = anterior[chave];
                const inicial = res.compra_inicial && chaveCompra(res.compra_inicial) === chave;
                nova[chave] = {
                    marcado: ja ? ja.marcado : (inicial && !c.corrida?.ativa),
                    numero_pedido: ja ? ja.numero_pedido : (c.numero_pedido || ''),
                    palavra_chave: ja ? ja.palavra_chave : (c.palavra_chave || ''),
                };
            });
            return nova;
        });
        setEtapa('revisao');

        getLalamoveServicos()
            .then((s) => {
                const mapa = {};
                (s.servicos || []).forEach((servico) => { mapa[servico.service_type] = servico.adicionais || []; });
                setAdicionaisPorServico(mapa);
            })
            .catch(() => setAdicionaisPorServico({}));
    }, [mostrarCorrida]);

    const preparar = useCallback(async (params) => {
        try {
            setEtapa('carregando');
            setErro(null);
            abrirPreparo(await prepararColetaLalamove(params));
        } catch (e) {
            setErro(e.response?.data?.error || 'Não foi possível preparar a coleta.');
            setEtapa('erro');
        }
    }, [abrirPreparo]);

    const carregarPontos = useCallback(async () => {
        try {
            setEtapa('carregando');
            const res = await getPontosColetaLalamove();
            setPontos((res.pontos || []).filter((p) => p.ativo));
            setFornecedores(res.fornecedores || { listas: [], furos: [] });
            setEtapa('escolherPonto');
        } catch (e) {
            setErro(e.response?.data?.error || 'Não foi possível carregar os endereços de coleta.');
            setEtapa('erro');
        }
    }, []);

    // Tipo e id, nao o objeto: a tela pode recriar o objeto a cada render.
    const compraTipo = compra?.tipo ?? null;
    const compraId = compra?.id ?? null;

    useEffect(() => {
        if (!isOpen) return;

        setErro(null);
        setPrep(null);
        setSelecao({});
        setCotacoes([]);
        setEscolhida(null);
        setOrdem(null);
        setComprasCorrida([]);
        setPrioridade(false);
        setRegrasGorjeta(null);
        setAdicionais([]);
        setSemEndereco(null);
        setJanela(7);
        expiraEm.current = null;

        if (ordemId) {
            setEtapa('carregando');
            getCorridaColetaLalamove(ordemId)
                .then(mostrarCorrida)
                .catch((e) => { setErro(e.response?.data?.error || 'Não foi possível abrir a corrida.'); setEtapa('erro'); });
        } else if (compraTipo && compraId) {
            preparar({ tipo: compraTipo, id: compraId, dias: 7 });
        } else {
            carregarPontos();
        }
    }, [isOpen, ordemId, compraTipo, compraId, preparar, carregarPontos, mostrarCorrida]);

    const trocarJanela = (dias) => {
        setJanela(dias);
        preparar(prep?.compra_inicial
            ? { tipo: prep.compra_inicial.tipo, id: prep.compra_inicial.id, dias }
            : { ponto_coleta_id: prep?.ponto?.id, dias });
    };

    // ── Revisao: pedidos, endereco e texto ──────────────────────────────

    const comprasDoPonto = useMemo(() => prep?.compras || [], [prep]);

    const selecionadas = useMemo(() => comprasDoPonto
        .filter((c) => selecao[chaveCompra(c)]?.marcado)
        .map((c) => ({ ...c, ...selecao[chaveCompra(c)] })), [comprasDoPonto, selecao]);

    const textoPadrao = useMemo(() => (prep ? textoMotorista({
        modelo: prep.modelo_texto,
        ponto: prep.ponto,
        compras: selecionadas,
        complemento: observacaoDoMotorista(origem),
    }) : ''), [prep, selecionadas, origem]);

    const texto = textoEditado ?? textoPadrao;
    const problemasEndereco = useMemo(() => impedimentosDoEndereco(origem, null), [origem]);
    const pendencias = [
        ...pendenciasDaSelecao(selecionadas, prep?.max_compras || 15),
        ...problemasEndereco,
        !texto.trim() && 'O texto para o motorista está vazio.',
        texto.length > LIMITE_TEXTO && `O texto para o motorista passou de ${LIMITE_TEXTO} caracteres.`,
    ].filter(Boolean);

    const enderecoMudou = useMemo(() => {
        if (!prep?.origem) return false;
        const a = paraPayload(origem);
        const b = paraPayload(doCadastro(prep.origem));
        return ['logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf', 'cep', 'referencia', 'lat', 'lng']
            .some((k) => String(a[k] ?? '') !== String(b[k] ?? ''));
    }, [origem, prep]);

    const alterarSelecao = (c, campo, valor) => {
        const chave = chaveCompra(c);
        setSelecao((s) => ({ ...s, [chave]: { ...(s[chave] || {}), [campo]: valor } }));
    };

    const payloadBase = (extra = {}) => ({
        ponto_coleta_id: prep?.ponto?.id,
        origem: paraPayload(origem),
        ...extra,
    });

    // ── Cotacao ─────────────────────────────────────────────────────────

    useEffect(() => {
        if (etapa !== 'cotacoes' || !expiraEm.current) return undefined;
        const tick = () => setRestante(segundosAte(expiraEm.current));
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [etapa]);

    const expirada = etapa === 'cotacoes' && restante <= 0;

    const cotar = async () => {
        if (pendencias.length > 0) {
            toast({ title: 'Falta conferir', description: pendencias[0], status: 'warning', duration: 7000, isClosable: true });
            return;
        }

        setAdicionais([]);
        setTotalSemAdicionais(null);
        setCotando(true);
        try {
            const res = await cotarColetaLalamove(payloadBase());
            setCotacoes(res.cotacoes || []);
            setFalhas(res.falhas || {});
            setRegrasGorjeta(res.aumento_gorjeta ? normalizarGorjeta(res.aumento_gorjeta) : null);
            expiraEm.current = res.cotacoes?.[0]?.expira_em || null;
            // Ja calcula o prazo aqui: esperar o contador deixava a cotacao
            // nascer "expirada" por um instante, com o Continuar travado.
            setRestante(segundosAte(expiraEm.current));
            // A mais barata vem marcada: compra costuma caber na moto.
            const maisBarata = [...(res.cotacoes || [])].sort((a, b) => a.total - b.total)[0];
            setEscolhida(maisBarata || null);
            setEtapa('cotacoes');
        } catch (e) {
            const d = e.response?.data || {};
            toast({
                title: d.endereco_invalido ? 'Endereço de coleta recusado' : 'Falha na cotação',
                description: d.error || 'Não foi possível cotar na Lalamove.',
                status: d.endereco_invalido ? 'warning' : 'error',
                duration: 9000,
                isClosable: true,
            });
        } finally {
            setCotando(false);
        }
    };

    const adicionaisDisponiveis = adicionaisPorServico[escolhida?.service_type] || [];
    const gruposDeAdicionais = adicionaisDisponiveis.reduce((acc, a) => {
        const chave = a.grupo || '_avulsos';
        (acc[chave] = acc[chave] || []).push(a);
        return acc;
    }, {});
    const rotuloDoAdicional = (nome) => adicionaisDisponiveis.find((a) => a.nome === nome)?.rotulo || nome;
    const diferencaAdicionais = totalSemAdicionais !== null && escolhida
        ? Number((escolhida.total - totalSemAdicionais).toFixed(2)) : 0;

    const aplicarAdicionais = async (novos) => {
        const anteriores = adicionais;
        setAdicionais(novos);
        setRecotando(true);
        if (totalSemAdicionais === null && escolhida) setTotalSemAdicionais(escolhida.total);

        try {
            const res = await cotarModalidadeColetaLalamove(payloadBase({ service_type: escolhida.service_type, adicionais: novos }));
            const nova = { ...res.cotacao, rotulo: escolhida.rotulo };
            setEscolhida(nova);
            setCotacoes((antigas) => antigas.map((c) => (c.service_type === nova.service_type ? nova : c)));
            expiraEm.current = nova.expira_em;
            setRestante(segundosAte(expiraEm.current));
        } catch (e) {
            setAdicionais(anteriores);
            toast({ title: 'Não foi possível cotar com esse adicional', description: e.response?.data?.error, status: 'error', duration: 7000, isClosable: true });
        } finally {
            setRecotando(false);
        }
    };

    // ── Confirmar ───────────────────────────────────────────────────────

    const regraEscolhida = regrasGorjeta ? regrasGorjeta[prioridade ? 'prioridade' : 'normal'] : null;
    const gorjetaInicial = regraEscolhida ? valorGorjeta(escolhida?.total, regraEscolhida, 0) : 0;
    const gorjetaMaxima = regraEscolhida ? tetoGorjeta(escolhida?.total, regraEscolhida) : 0;
    const totalAgora = Number(escolhida?.total || 0) + gorjetaInicial;
    const saldo = prep?.carteira?.saldo;
    const saldoInsuficiente = saldo !== null && saldo !== undefined && saldo < totalAgora;

    const confirmar = async () => {
        if (!escolhida) return;
        setConfirmando(true);
        try {
            const res = await criarColetaLalamove(payloadBase({
                quotation_id: escolhida.quotation_id,
                service_type: escolhida.service_type,
                preco_confirmado: escolhida.total,
                compras: selecionadas.map((c) => ({
                    tipo: c.tipo, id: c.id, numero_pedido: c.numero_pedido.trim(), palavra_chave: c.palavra_chave?.trim() || null,
                })),
                texto_motorista: texto.trim(),
                adicionais: adicionais.length ? adicionais : undefined,
                prioridade: regrasGorjeta ? prioridade : undefined,
                salvar_endereco: enderecoMudou && salvarEndereco,
            }));

            mostrarCorrida({ ...res, ponto: prep?.ponto });
            avisarMudanca();
            const g = res.ordem?.gorjeta;
            const inicialFalhou = g && g.erro && prioridade;
            toast({
                title: 'Coleta solicitada',
                description: inicialFalhou
                    ? `A corrida foi criada, mas a gorjeta inicial não entrou: ${g.erro}`
                    : `${selecionadas.length} pedido(s) de ${prep?.ponto?.nome}. A Lalamove já está procurando motorista.`,
                status: inicialFalhou ? 'warning' : 'success',
                duration: inicialFalhou ? 12000 : 6000,
                isClosable: true,
            });
        } catch (e) {
            const d = e.response?.data || {};

            if (d.recotar && d.cotacao) {
                setEscolhida(d.cotacao);
                setCotacoes((antigas) => antigas.map((c) => (c.service_type === d.cotacao.service_type ? { ...d.cotacao, rotulo: c.rotulo } : c)));
                expiraEm.current = d.cotacao.expira_em;
                setRestante(segundosAte(expiraEm.current));
                setEtapa('cotacoes');
                toast({ title: 'Cotação atualizada', description: d.error, status: 'warning', duration: 8000, isClosable: true });
                return;
            }
            if (d.preco_mudou) {
                setEscolhida((c) => (c ? { ...c, total: d.preco_atual } : c));
                setEtapa('cotacoes');
                toast({ title: 'O preço mudou', description: d.error, status: 'warning', duration: 10000, isClosable: true });
                return;
            }
            if (d.endereco_invalido) {
                setEtapa('revisao');
                toast({ title: 'Endereço de coleta recusado', description: d.error, status: 'warning', duration: null, isClosable: true });
                return;
            }
            if (d.pendente) {
                toast({ title: 'Confira na Lalamove antes de pedir de novo', description: d.error, status: 'warning', duration: null, isClosable: true });
                avisarMudanca();
                preparar(prep?.compra_inicial ? { tipo: prep.compra_inicial.tipo, id: prep.compra_inicial.id, dias: janela } : { ponto_coleta_id: prep?.ponto?.id, dias: janela });
                return;
            }

            const validacao = d.errors ? Object.values(d.errors).flat()[0] : null;
            toast({ title: 'Não foi possível pedir a coleta', description: d.error || validacao || d.message || 'Erro inesperado.', status: 'error', duration: 9000, isClosable: true });
        } finally {
            setConfirmando(false);
        }
    };

    // ── Corrida criada ──────────────────────────────────────────────────

    const recarregarCorrida = useCallback(async (atualizar = false) => {
        if (!ordem?.id) return;
        const res = await getCorridaColetaLalamove(ordem.id, atualizar);
        setOrdem(res.ordem);
        setComprasCorrida(res.compras || []);
        if (res.ponto) setPontoCorrida(res.ponto);
    }, [ordem?.id]);

    const atualizar = async () => {
        setAtualizando(true);
        try {
            await recarregarCorrida(true);
            avisarMudanca();
        } catch (e) {
            toast({ title: 'Falha ao atualizar', description: e.response?.data?.error, status: 'error', isClosable: true });
        } finally {
            setAtualizando(false);
        }
    };

    const procurando = isOpen && etapa === 'criada' && ['ASSIGNING_DRIVER', 'PENDENTE'].includes(ordem?.status) && !confirmarNaLalamove;
    useEffect(() => {
        if (!procurando) return undefined;
        const ler = async () => {
            if (document.hidden) return;
            try { await recarregarCorrida(false); } catch (e) { /* proximo ciclo tenta de novo */ }
        };
        const id = setInterval(ler, 20000);
        document.addEventListener('visibilitychange', ler);
        return () => { clearInterval(id); document.removeEventListener('visibilitychange', ler); };
    }, [procurando, recarregarCorrida]);

    const alvo = { vendaId: null, ordemId: ordem?.id ?? null };

    const aumentarGorjetaAgora = async () => {
        try {
            const res = await aumentarGorjetaAgoraLalamove(alvo);
            if (res?.ordem) setOrdem(res.ordem);
            toast({ title: `Gorjeta aumentada para ${brl(res?.ordem?.gorjeta?.atual)}`, status: 'success', duration: 6000, isClosable: true });
            return { ok: true };
        } catch (e) {
            const d = e.response?.data || {};
            if (d.ordem) setOrdem(d.ordem);
            return { ok: false, erro: d.error || 'Falha de comunicação. Tente de novo.' };
        }
    };

    const pausarGorjeta = async (pausar) => {
        setPausandoGorjeta(true);
        try {
            const res = await pausarAumentoGorjetaLalamove(alvo, pausar);
            if (res?.ordem) setOrdem(res.ordem);
            toast({
                title: pausar ? 'Aumentos parados' : 'Aumentos retomados',
                description: pausar ? `A gorjeta fica em ${brl(res?.ordem?.gorjeta?.atual)}. Dá para retomar a qualquer momento.` : undefined,
                status: pausar ? 'info' : 'success',
                duration: 6000,
                isClosable: true,
            });
        } catch (e) {
            const d = e.response?.data || {};
            if (d.ordem) setOrdem(d.ordem);
            toast({ title: 'Não foi possível mudar os aumentos', description: d.error, status: 'error', duration: 9000, isClosable: true });
        } finally {
            setPausandoGorjeta(false);
        }
    };

    const executarAcao = async () => {
        if (!acao) return;
        setExecutando(true);
        try {
            if (acao.tipo === 'cancelar') {
                const res = await cancelarOrdemLalamove(alvo, 'Coleta cancelada pelo operador no TAGA Admin');
                setOrdem(res.ordem);
                toast({ title: 'Coleta cancelada', description: 'Os pedidos voltam a ficar livres para outra corrida.', status: 'success', isClosable: true });
            } else if (acao.tipo === 'desvincular') {
                const res = await desvincularCompraColetaLalamove(acao.alvo.vinculo_id);
                setComprasCorrida(res.compras || []);
                toast({ title: `${acao.alvo.rotulo} saiu da corrida`, description: 'O custo da corrida continua registrado. A retirada da compra não foi desfeita.', status: 'success', duration: 7000, isClosable: true });
            } else if (acao.tipo === 'descartar') {
                await descartarPendenteLalamove(alvo);
                toast({ title: 'Solicitação descartada', description: 'Os pedidos voltam a ficar livres para outra corrida.', status: 'success', isClosable: true });
                setAcao(null);
                avisarMudanca();
                onClose();
                return;
            } else if (acao.tipo === 'trocar') {
                const res = await trocarMotoristaLalamove(alvo, motivoTroca);
                if (res?.ordem) setOrdem(res.ordem);
                toast({ title: 'Motorista dispensado', description: res?.mensagem || 'A Lalamove já está procurando outro.', status: 'success', duration: 6000, isClosable: true });
            }
            setAcao(null);
            avisarMudanca();
        } catch (e) {
            const d = e.response?.data || {};
            if (d.ordem) setOrdem(d.ordem);
            toast({ title: 'Não foi possível concluir', description: d.error || 'Erro inesperado.', status: 'error', duration: 9000, isClosable: true });
        } finally {
            setExecutando(false);
        }
    };

    // ── Render ──────────────────────────────────────────────────────────

    const pontoAtual = prep?.ponto || pontoCorrida;

    const cabecalho = (
        <ModalHeader borderBottomWidth="1px" py={3} pr={12}>
            <HStack spacing={2} wrap="wrap">
                <Image src="/lalamove_logo.webp" alt="Lalamove" h="16px" w="auto" objectFit="contain" />
                <Text fontSize="md" fontWeight="bold">Coleta no fornecedor</Text>
                {pontoAtual && <Badge colorScheme="orange" variant="subtle">{pontoAtual.nome}</Badge>}
                {ordem?.order_id && <Badge colorScheme={CORES_STATUS[ordem.status] || 'gray'}>{ordem.status_rotulo}</Badge>}
            </HStack>
        </ModalHeader>
    );

    const trajeto = (deOnde, paraOnde) => (
        <Box borderWidth="1px" borderRadius="lg" p={3} bg="gray.50">
            <HStack align="start" spacing={3}>
                <VStack spacing={0} pt={1}>
                    <Icon as={FaStore} color="orange.500" boxSize={3} />
                    <Box w="1px" h="22px" bg="gray.300" my={1} />
                    <Icon as={FaArrowDown} color="purple.400" boxSize={3} />
                </VStack>
                <VStack align="stretch" spacing={2} flex={1} minW={0}>
                    <Box>
                        <Text fontSize="10px" color="gray.500" fontWeight="bold" textTransform="uppercase">Coleta</Text>
                        <Text fontSize="sm" noOfLines={2}>{deOnde}</Text>
                    </Box>
                    <Box>
                        <Text fontSize="10px" color="gray.500" fontWeight="bold" textTransform="uppercase">Entrega na loja</Text>
                        <Text fontSize="sm" noOfLines={2}>{paraOnde}</Text>
                    </Box>
                </VStack>
            </HStack>
        </Box>
    );

    const renderEscolherPonto = () => {
        const termo = buscaPonto.trim().toLowerCase();
        const lista = pontos.filter((p) => !termo || [p.nome, p.fornecedor_nome, p.cotacao_fornecedor_nome].some((v) => String(v || '').toLowerCase().includes(termo)));

        return (
            <VStack align="stretch" spacing={3}>
                <Text fontSize="sm" color="gray.600">De qual fornecedor a Lalamove vai buscar a compra?</Text>
                <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none"><Icon as={FaSearch} color="gray.400" boxSize={3} /></InputLeftElement>
                    <Input borderRadius="lg" placeholder="Buscar fornecedor" value={buscaPonto} onChange={(e) => setBuscaPonto(e.target.value)} autoFocus={!compacto} />
                </InputGroup>
                {lista.length === 0 ? (
                    <Text fontSize="sm" color="gray.500" py={4} textAlign="center">
                        {pontos.length === 0 ? 'Nenhum endereço de coleta cadastrado ainda.' : 'Nenhum fornecedor encontrado.'}
                    </Text>
                ) : (
                    <VStack align="stretch" spacing={2} maxH={compacto ? undefined : '46vh'} overflowY="auto">
                        {lista.map((p) => (
                            <Box key={p.id} as="button" type="button" textAlign="left" borderWidth="1px" borderRadius="lg" p={3}
                                transition="all 0.15s" _hover={{ borderColor: 'purple.300', bg: 'purple.50' }}
                                onClick={() => preparar({ ponto_coleta_id: p.id, dias: janela })}>
                                <HStack justify="space-between">
                                    <HStack spacing={2} minW={0}>
                                        <Icon as={FaStore} color="orange.400" boxSize={3} />
                                        <Text fontWeight="bold" fontSize="sm" noOfLines={1}>{p.nome}</Text>
                                    </HStack>
                                    <Text fontSize="xs" color="gray.400" flexShrink={0}>{p.corridas} corrida(s)</Text>
                                </HStack>
                                <Text fontSize="xs" color="gray.500" noOfLines={1} mt={1}>{p.endereco?.linha}</Text>
                            </Box>
                        ))}
                    </VStack>
                )}
                <Button size="sm" variant="outline" leftIcon={<Icon as={FaPlus} boxSize={3} />}
                    onClick={() => { setSemEndereco({ fornecedor: null }); setEtapa('semEndereco'); }}>
                    Cadastrar outro fornecedor
                </Button>
            </VStack>
        );
    };

    const renderSemEndereco = () => (
        <VStack align="stretch" spacing={4}>
            {semEndereco?.fornecedor && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    {semEndereco.fornecedor.nome} ainda não tem endereço de coleta. Cadastre uma vez e ele fica salvo para as próximas corridas.
                </Alert>
            )}
            <FormEnderecoColeta
                fornecedores={fornecedores}
                fornecedorFixo={semEndereco?.fornecedor || null}
                compacto={compacto}
                onCancelar={compra ? onClose : () => carregarPontos()}
                onSalvo={(p) => preparar(compra ? { tipo: compra.tipo, id: compra.id, dias: janela } : { ponto_coleta_id: p.id, dias: janela })}
            />
        </VStack>
    );

    const renderRevisao = () => (
        <VStack align="stretch" spacing={5}>
            {trajeto(linhaLalamove(origem) || prep?.ponto?.endereco?.linha, prep?.loja?.endereco)}

            <Box>
                <Flex justify="space-between" align="center" mb={2} gap={2} wrap="wrap">
                    <HStack spacing={2}>
                        <Icon as={FaBoxOpen} color="orange.500" boxSize={3} />
                        <Text fontSize="sm" fontWeight="bold">Pedidos desta coleta</Text>
                        <Badge colorScheme="purple" borderRadius="full">{selecionadas.length}</Badge>
                    </HStack>
                    <HStack spacing={2}>
                        <Text fontSize="xs" color="gray.500">Liberados nos últimos</Text>
                        <Select size="xs" w="88px" borderRadius="md" value={janela} onChange={(e) => trocarJanela(Number(e.target.value))}>
                            {JANELAS.map((d) => <option key={d} value={d}>{d} dias</option>)}
                        </Select>
                    </HStack>
                </Flex>
                <Text fontSize="xs" color="gray.500" mb={3}>
                    Marque o que o motorista vai buscar de uma vez. O número do pedido do fornecedor é obrigatório; a palavra-chave
                    só quando o fornecedor exige (ex.: MSAM).
                </Text>

                {comprasDoPonto.length === 0 ? (
                    <Alert status="info" borderRadius="md" fontSize="sm"><AlertIcon />Nenhum pedido a retirar deste fornecedor no período.</Alert>
                ) : (
                    <VStack align="stretch" spacing={2}>
                        {comprasDoPonto.map((c) => {
                            const chave = chaveCompra(c);
                            const s = selecao[chave] || {};
                            const ocupado = c.corrida?.ativa;
                            const semNumero = s.marcado && !String(s.numero_pedido || '').trim();

                            return (
                                <Box key={chave} borderWidth="1px" borderRadius="lg" p={3}
                                    borderColor={s.marcado ? 'purple.300' : 'gray.200'} bg={s.marcado ? 'purple.50' : ocupado ? 'gray.50' : 'white'}
                                    opacity={ocupado ? 0.75 : 1} transition="all 0.15s">
                                    <Flex justify="space-between" align="start" gap={2} wrap="wrap">
                                        <Checkbox colorScheme="purple" isChecked={!!s.marcado} isDisabled={ocupado}
                                            onChange={(e) => alterarSelecao(c, 'marcado', e.target.checked)}>
                                            <HStack spacing={2} wrap="wrap">
                                                <Text fontSize="sm" fontWeight="bold">{c.rotulo}</Text>
                                                <Badge colorScheme={c.tipo === 'lista' ? 'teal' : 'orange'} fontSize="9px">
                                                    {c.tipo === 'lista' ? 'Lista de compras' : 'Furo de estoque'}
                                                </Badge>
                                            </HStack>
                                        </Checkbox>
                                        <HStack spacing={3} fontSize="xs" color="gray.500">
                                            <Text>{dataCurta(c.data)}</Text>
                                            <Text>{c.itens} item(ns)</Text>
                                            {c.valor > 0 && <Text fontWeight="semibold" color="gray.700">{brl(c.valor)}</Text>}
                                        </HStack>
                                    </Flex>

                                    {ocupado && (
                                        <Text fontSize="xs" color="orange.600" mt={1}>
                                            Já está numa corrida em andamento ({c.corrida.status_rotulo}).
                                        </Text>
                                    )}
                                    {!ocupado && c.corrida && !c.corrida.ativa && (
                                        <Text fontSize="xs" color="gray.500" mt={1}>Última corrida: {c.corrida.status_rotulo}.</Text>
                                    )}

                                    {s.marcado && (
                                        <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2} mt={2}>
                                            <FormControl isRequired isInvalid={semNumero}>
                                                <FormLabel htmlFor={`num-${chave}`} fontSize="xs" color="gray.600" mb={1}>Nº do pedido do fornecedor</FormLabel>
                                                <Input id={`num-${chave}`} size="sm" bg="white" maxLength={100} value={s.numero_pedido || ''}
                                                    onChange={(e) => alterarSelecao(c, 'numero_pedido', e.target.value)} placeholder="Ex.: 1602308" />
                                            </FormControl>
                                            <FormControl>
                                                <FormLabel htmlFor={`chave-${chave}`} fontSize="xs" color="gray.600" mb={1}>Palavra-chave (opcional)</FormLabel>
                                                <Input id={`chave-${chave}`} size="sm" bg="white" maxLength={60} value={s.palavra_chave || ''}
                                                    onChange={(e) => alterarSelecao(c, 'palavra_chave', e.target.value)} placeholder="Ex.: FERRO" />
                                            </FormControl>
                                        </SimpleGrid>
                                    )}
                                </Box>
                            );
                        })}
                    </VStack>
                )}
            </Box>

            <Box>
                <HStack spacing={2} mb={2}>
                    <Icon as={FaMapMarkerAlt} color="orange.500" boxSize={3} />
                    <Text fontSize="sm" fontWeight="bold">Endereço de coleta</Text>
                </HStack>
                <SeletorEnderecoMapa
                    aberto={isOpen}
                    compacto={compacto}
                    alturaMapa={compacto ? 200 : 240}
                    destinoInicial={origem}
                    cadastro={null}
                    onChange={setOrigem}
                />
                {enderecoMudou && (
                    <Checkbox mt={2} size="sm" colorScheme="purple" isChecked={salvarEndereco} onChange={(e) => setSalvarEndereco(e.target.checked)}>
                        <Text fontSize="xs">Atualizar o cadastro de {prep?.ponto?.nome} com este endereço</Text>
                    </Checkbox>
                )}
            </Box>

            <Box>
                <Flex justify="space-between" align="center" mb={1} gap={2} wrap="wrap">
                    <HStack spacing={2}>
                        <Icon as={FaUser} color="orange.500" boxSize={3} />
                        <Text fontSize="sm" fontWeight="bold">Texto para o motorista</Text>
                    </HStack>
                    <HStack spacing={2}>
                        <Text fontSize="xs" color={texto.length > LIMITE_TEXTO ? 'red.500' : 'gray.400'}>{texto.length}/{LIMITE_TEXTO}</Text>
                        {textoEditado !== null && (
                            <Button size="xs" variant="ghost" onClick={() => setTextoEditado(null)}>Voltar ao texto padrão</Button>
                        )}
                    </HStack>
                </Flex>
                <Textarea size="sm" rows={Math.min(10, Math.max(4, texto.split('\n').length + 1))} fontFamily="mono" fontSize="xs"
                    value={texto} onChange={(e) => setTextoEditado(e.target.value)} />
                <Text fontSize="xs" color="gray.500" mt={1}>
                    No ponto de coleta, o motorista vê como remetente: <b>{nomeRemetente(prep?.ponto, selecionadas) || '-'}</b>
                </Text>
                {textoEditado !== null && textoEditado !== textoPadrao && (
                    <Text fontSize="xs" color="orange.600" mt={1}>Texto editado à mão: mudar os pedidos marcados não atualiza ele.</Text>
                )}
            </Box>
        </VStack>
    );

    const renderAdicionais = () => {
        if (!escolhida || adicionaisDisponiveis.length === 0) return null;
        return (
            <Box borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={3} bg="gray.50">
                <Flex justify="space-between" align="center" gap={2} mb={2} wrap="wrap">
                    <HStack spacing={2}>
                        <Icon as={FaPlus} color="gray.400" boxSize={3} />
                        <Text fontSize="sm" fontWeight="bold" color="gray.700">Adicionais de {escolhida.rotulo}</Text>
                        {recotando && <Spinner size="xs" color="purple.500" />}
                    </HStack>
                    {adicionais.length > 0 && <Button size="xs" variant="ghost" onClick={() => aplicarAdicionais([])} isDisabled={recotando}>limpar</Button>}
                </Flex>
                <VStack align="stretch" spacing={3}>
                    {Object.entries(gruposDeAdicionais).map(([grupo, itens]) => (
                        <Box key={grupo}>
                            {grupo !== '_avulsos' && <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>{grupo}</Text>}
                            {grupo === '_avulsos' ? (
                                <VStack align="stretch" spacing={1}>
                                    {itens.map((a) => (
                                        <Checkbox key={a.nome} size="sm" colorScheme="purple" isChecked={adicionais.includes(a.nome)} isDisabled={recotando}
                                            onChange={() => aplicarAdicionais(adicionais.includes(a.nome) ? adicionais.filter((x) => x !== a.nome) : [...adicionais, a.nome])}>
                                            <Text fontSize="sm">{a.rotulo}</Text>
                                        </Checkbox>
                                    ))}
                                </VStack>
                            ) : (
                                <Select size="sm" bg="white" borderRadius="lg" isDisabled={recotando}
                                    value={itens.find((a) => adicionais.includes(a.nome))?.nome || ''}
                                    onChange={(e) => {
                                        const nomes = itens.map((a) => a.nome);
                                        const sem = adicionais.filter((a) => !nomes.includes(a));
                                        aplicarAdicionais(e.target.value ? [...sem, e.target.value] : sem);
                                    }}>
                                    <option value="">sem esse adicional</option>
                                    {itens.map((a) => <option key={a.nome} value={a.nome}>{a.rotulo}</option>)}
                                </Select>
                            )}
                        </Box>
                    ))}
                </VStack>
                {adicionais.length > 0 && diferencaAdicionais !== 0 && (
                    <Alert status="info" borderRadius="md" fontSize="xs" mt={3}><AlertIcon />Os adicionais somaram&nbsp;<b>{brl(diferencaAdicionais)}</b>&nbsp;ao preço.</Alert>
                )}
            </Box>
        );
    };

    const renderCotacoes = () => (
        <VStack align="stretch" spacing={3}>
            <HStack justify="space-between" wrap="wrap" spacing={2}>
                <Text fontSize="sm" color="gray.600">{selecionadas.length} pedido(s) de {prep?.ponto?.nome}</Text>
                <HStack spacing={2}>
                    <Badge colorScheme={expirada ? 'red' : restante < 60 ? 'orange' : 'green'} variant="subtle">
                        {expirada ? 'Cotação expirada' : `Expira em ${Math.floor(restante / 60)}:${String(restante % 60).padStart(2, '0')}`}
                    </Badge>
                    <Button size="xs" leftIcon={<RepeatIcon />} onClick={cotar} isLoading={cotando} variant="outline">Recotar</Button>
                </HStack>
            </HStack>

            <VStack align="stretch" spacing={2} maxH={compacto ? undefined : '40vh'} overflowY={compacto ? 'visible' : 'auto'} pr={1}>
                {cotacoes.map((c) => {
                    const ativa = escolhida?.service_type === c.service_type;
                    return (
                        <Box key={c.service_type} borderWidth="2px" borderColor={ativa ? 'purple.400' : 'gray.200'} bg={ativa ? 'purple.50' : 'white'}
                            borderRadius="lg" p={3} cursor="pointer" transition="all 0.15s" _hover={{ borderColor: ativa ? 'purple.400' : 'purple.200', shadow: 'sm' }}
                            onClick={() => { if (!ativa) { setEscolhida(c); setAdicionais([]); setTotalSemAdicionais(null); } }}>
                            <Flex justify="space-between" align="center" gap={3}>
                                <HStack spacing={3} minW={0}>
                                    <Icon as={ICONE_SERVICO[c.service_type] || FaTruck} color={ativa ? 'purple.500' : 'gray.400'} boxSize={5} />
                                    <VStack align="start" spacing={0} minW={0}>
                                        <Text fontSize="sm" fontWeight="bold" color="gray.800">{c.rotulo}</Text>
                                        <Text fontSize="xs" color="gray.500">{c.distancia_m ? `${(c.distancia_m / 1000).toFixed(1)} km` : 'distância indisponível'}</Text>
                                    </VStack>
                                </HStack>
                                <VStack align="end" spacing={0}>
                                    <Text fontSize="xs" color="gray.500">custo</Text>
                                    <Text fontSize="md" fontWeight="bold" color="gray.800">{brl(c.total)}</Text>
                                </VStack>
                            </Flex>
                        </Box>
                    );
                })}
            </VStack>

            {renderAdicionais()}

            {Object.keys(falhas).length > 0 && <Text fontSize="xs" color="gray.500">Sem preço para: {Object.keys(falhas).join(', ')}</Text>}
        </VStack>
    );

    const renderConfirmacao = () => (
        <VStack align="stretch" spacing={4}>
            <Alert status="warning" borderRadius="md" fontSize="sm">
                <AlertIcon />
                A Lalamove cobra na hora. Depois que o motorista aceitar, ou passados 5 minutos, o valor não volta.
            </Alert>

            {saldoInsuficiente && (
                <Alert status="error" borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    O último saldo informado pela Lalamove é {brl(saldo)}, menor que o valor da corrida. Recarregue antes no portal da Lalamove ou a corrida pode ser recusada.
                </Alert>
            )}

            {regrasGorjeta && (
                <EscolhaPrioridade regras={regrasGorjeta} original={Number(escolhida?.total || 0)} prioridade={prioridade} onChange={setPrioridade} isDisabled={confirmando} />
            )}

            <Box borderWidth="2px" borderColor={prioridade ? 'orange.200' : 'purple.200'} bg={prioridade ? 'orange.50' : 'purple.50'} borderRadius="lg" p={4} textAlign="center">
                <Text fontSize="xs" color={prioridade ? 'orange.700' : 'purple.600'} fontWeight="bold" textTransform="uppercase">Você vai pagar agora</Text>
                <Text fontSize="3xl" fontWeight="bold" color={prioridade ? 'orange.700' : 'purple.700'} lineHeight="1.2">{brl(totalAgora)}</Text>
                <Text fontSize="sm" color={prioridade ? 'orange.700' : 'purple.600'}>
                    {escolhida?.rotulo}{gorjetaInicial > 0 && ` · corrida ${brl(escolhida?.total)} + gorjeta ${brl(gorjetaInicial)}`}
                </Text>
                {gorjetaMaxima > gorjetaInicial && (
                    <Text fontSize="xs" color="gray.600" mt={1}>Se ninguém aceitar, pode chegar a <b>{brl(Number(escolhida?.total || 0) + gorjetaMaxima)}</b></Text>
                )}
            </Box>

            <VStack align="stretch" spacing={2} fontSize="sm">
                <HStack justify="space-between" align="start">
                    <Text color="gray.600" flexShrink={0}>Coleta</Text>
                    <Text textAlign="right" maxW="70%" fontWeight="medium">{escolhida?.stops?.[0]?.endereco || linhaLalamove(origem)}</Text>
                </HStack>
                <HStack justify="space-between" align="start">
                    <Text color="gray.600" flexShrink={0}>Entrega</Text>
                    <Text textAlign="right" maxW="70%">{prep?.loja?.endereco}</Text>
                </HStack>
                <Divider />
                <HStack justify="space-between" align="start">
                    <Text color="gray.600" flexShrink={0}>Pedidos</Text>
                    <VStack align="end" spacing={0} maxW="70%">
                        {selecionadas.map((c) => (
                            <Text key={chaveCompra(c)} fontSize="sm" textAlign="right">
                                {c.rotulo} · <b>{c.numero_pedido}</b>{c.palavra_chave ? ` (${c.palavra_chave})` : ''}
                            </Text>
                        ))}
                    </VStack>
                </HStack>
                {adicionais.length > 0 && (
                    <HStack justify="space-between" align="start">
                        <Text color="gray.600" flexShrink={0}>Adicionais</Text>
                        <Text textAlign="right" maxW="70%" fontSize="sm">{adicionais.map(rotuloDoAdicional).join(', ')}</Text>
                    </HStack>
                )}
                {escolhida?.distancia_m && (
                    <HStack justify="space-between"><Text color="gray.600">Distância</Text><Text fontSize="xs" color="gray.500">{(escolhida.distancia_m / 1000).toFixed(1)} km</Text></HStack>
                )}
            </VStack>

            <Box borderWidth="1px" borderRadius="md" p={3} bg="gray.50">
                <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={1}>O motorista lê</Text>
                <Text fontSize="xs" fontFamily="mono" whiteSpace="pre-wrap">{texto}</Text>
            </Box>
        </VStack>
    );

    const renderCriada = () => {
        if (confirmarNaLalamove) {
            return (
                <VStack align="stretch" spacing={4}>
                    <Alert status="warning" borderRadius="md" fontSize="sm">
                        <AlertIcon />
                        <Box>
                            <Text fontWeight="bold">A Lalamove não confirmou esta solicitação.</Text>
                            <Text>A corrida pode ter sido criada mesmo assim. Confira no painel da Lalamove antes de pedir de novo, para não pagar duas vezes.</Text>
                        </Box>
                    </Alert>
                    {ordem?.erro_criacao && <Text fontSize="xs" color="gray.500">Erro registrado: {ordem.erro_criacao}</Text>}
                    <Button colorScheme="orange" variant="outline" onClick={() => setAcao({ tipo: 'descartar' })}>Conferi, a corrida não foi criada</Button>
                </VStack>
            );
        }

        return (
            <VStack align="stretch" spacing={4}>
                <Box borderWidth="1px" borderRadius="lg" p={4} bg={ordem?.status === 'COMPLETED' ? 'green.50' : 'gray.50'}>
                    <HStack justify="space-between" mb={2} wrap="wrap" spacing={2}>
                        <HStack spacing={2}>
                            <Icon as={ordem?.status === 'COMPLETED' ? FaCheckCircle : FaClipboardCheck} color={`${CORES_STATUS[ordem?.status] || 'gray'}.500`} />
                            <Text fontWeight="bold" fontSize="sm">{ordem?.status_rotulo}</Text>
                        </HStack>
                        <HStack spacing={2}>
                            <Badge colorScheme="purple" variant="subtle">{ordem?.rotulo}</Badge>
                            <Tooltip label="Atualizar status" hasArrow>
                                <IconButton aria-label="Atualizar" icon={<RepeatIcon />} size="sm" variant="ghost" onClick={atualizar} isLoading={atualizando} />
                            </Tooltip>
                        </HStack>
                    </HStack>
                    <HStack spacing={6} wrap="wrap">
                        <VStack align="start" spacing={0}>
                            <Text fontSize="xs" color="gray.500">Custo</Text>
                            <Text fontSize="sm" fontWeight="bold">{brl(ordem?.preco_total)}</Text>
                            {ordem?.priority_fee > 0 && <Text fontSize="10px" color="gray.500">inclui gorjeta {brl(ordem.priority_fee)}</Text>}
                        </VStack>
                        {ordem?.order_id && (
                            <VStack align="start" spacing={0}>
                                <Text fontSize="xs" color="gray.500">Código</Text>
                                <Text fontSize="xs" fontFamily="mono">{ordem.order_id}</Text>
                            </VStack>
                        )}
                    </HStack>
                </Box>

                {pontoCorrida && trajeto(pontoCorrida.endereco?.linha, prep?.loja?.endereco || 'Loja TAGA')}

                {ordem?.gorjeta && <PainelGorjeta ordem={ordem} onPausar={pausarGorjeta} pausando={pausandoGorjeta} onAumentar={aumentarGorjetaAgora} />}

                {(ordem?.motorista?.nome || ordem?.motorista?.telefone) && (
                    <Box borderWidth="1px" borderRadius="lg" p={3}>
                        <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={2}>Motorista</Text>
                        <HStack spacing={5} wrap="wrap" fontSize="sm">
                            <HStack spacing={2}><Icon as={FaUser} color="gray.400" boxSize={3} /><Text>{ordem.motorista.nome || '-'}</Text></HStack>
                            <HStack spacing={2}><Icon as={FaPhone} color="gray.400" boxSize={3} /><Text>{ordem.motorista.telefone || '-'}</Text></HStack>
                            {ordem.motorista.placa && <Badge colorScheme="gray">{ordem.motorista.placa}</Badge>}
                        </HStack>
                    </Box>
                )}

                <Box borderWidth="1px" borderRadius="lg" p={3}>
                    <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" mb={2}>Pedidos nesta corrida</Text>
                    {comprasCorrida.length === 0 ? (
                        <Text fontSize="sm" color="gray.500">Nenhum pedido vinculado.</Text>
                    ) : (
                        <VStack align="stretch" spacing={2}>
                            {comprasCorrida.map((c) => (
                                <Flex key={c.vinculo_id} justify="space-between" align="center" gap={2} wrap="wrap" borderBottomWidth="1px" borderColor="gray.50" pb={2}>
                                    <Box minW={0}>
                                        <HStack spacing={2} wrap="wrap">
                                            <Text fontSize="sm" fontWeight="bold">{c.rotulo}</Text>
                                            <Badge colorScheme={c.retirada ? 'purple' : 'green'} fontSize="9px">{c.status_rotulo}</Badge>
                                        </HStack>
                                        <Text fontSize="xs" color="gray.500">
                                            {c.fornecedor_nome}{c.numero_pedido ? ` · pedido ${c.numero_pedido}` : ''}{c.palavra_chave ? ` · ${c.palavra_chave}` : ''}
                                        </Text>
                                    </Box>
                                    <Button size="xs" variant="ghost" colorScheme="red" leftIcon={<Icon as={FaUnlink} boxSize={2.5} />}
                                        onClick={() => setAcao({ tipo: 'desvincular', alvo: c })}>
                                        Tirar da corrida
                                    </Button>
                                </Flex>
                            ))}
                        </VStack>
                    )}
                    <Text fontSize="xs" color="gray.400" mt={2}>
                        Quando o motorista coleta, os pedidos vão sozinhos para Retirado com o link de rastreio.
                    </Text>
                </Box>

                {ordem?.share_link && (
                    <Button as={ChakraLink} href={ordem.share_link} isExternal colorScheme="purple" variant="outline" size="sm" rightIcon={<ExternalLinkIcon />}>
                        Acompanhar a corrida ao vivo
                    </Button>
                )}
                {ordem?.pod_imagem && (
                    <ChakraLink href={ordem.pod_imagem} isExternal fontSize="sm" color="purple.600" fontWeight="bold">
                        Ver comprovante de entrega <ExternalLinkIcon boxSize={3} />
                    </ChakraLink>
                )}
                {ordem?.motivo_cancelamento && <Text fontSize="xs" color="gray.500">Motivo do cancelamento: {ordem.motivo_cancelamento}</Text>}
            </VStack>
        );
    };

    const renderCorpo = () => {
        if (etapa === 'carregando') {
            return (
                <VStack spacing={3} py={6}>
                    <Spinner color="purple.500" />
                    <Text fontSize="sm" color="gray.500">Carregando...</Text>
                    <Skeleton height="60px" w="100%" borderRadius="md" />
                    <Skeleton height="60px" w="100%" borderRadius="md" />
                </VStack>
            );
        }
        if (etapa === 'erro') return <Alert status="error" borderRadius="md" fontSize="sm"><AlertIcon />{erro}</Alert>;
        if (etapa === 'escolherPonto') return renderEscolherPonto();
        if (etapa === 'semEndereco') return renderSemEndereco();
        if (etapa === 'revisao') return renderRevisao();
        if (etapa === 'cotacoes') return renderCotacoes();
        if (etapa === 'confirmacao') return renderConfirmacao();
        if (etapa === 'criada') return renderCriada();
        return null;
    };

    const renderRodape = () => {
        if (etapa === 'revisao') {
            return (
                <Flex w="100%" justify="space-between" align="center" gap={2} wrap="wrap">
                    <Text fontSize="xs" color={pendencias.length ? 'orange.600' : 'green.600'} flex={1} minW="180px">
                        {pendencias.length ? pendencias[0] : `${selecionadas.length} pedido(s) conferido(s)`}
                    </Text>
                    <HStack spacing={2}>
                        {!compra && <Button variant="ghost" size="sm" onClick={carregarPontos}>Trocar fornecedor</Button>}
                        <Button colorScheme="purple" size="sm" onClick={cotar} isLoading={cotando} loadingText="Cotando" isDisabled={pendencias.length > 0}>
                            Cotar modalidades
                        </Button>
                    </HStack>
                </Flex>
            );
        }
        if (etapa === 'cotacoes') {
            return (
                <HStack w="100%" justify="space-between" spacing={2}>
                    <Button variant="ghost" size="sm" onClick={() => setEtapa('revisao')}>Voltar</Button>
                    <Button colorScheme="purple" size="sm" onClick={() => setEtapa('confirmacao')} isDisabled={!escolhida || expirada || recotando}>Continuar</Button>
                </HStack>
            );
        }
        if (etapa === 'confirmacao') {
            return (
                <HStack w="100%" justify="space-between" spacing={2}>
                    <Button variant="ghost" size="sm" onClick={() => setEtapa('cotacoes')} isDisabled={confirmando}>Voltar</Button>
                    <Button colorScheme={prioridade ? 'orange' : 'purple'} size="sm" onClick={confirmar} isLoading={confirmando} loadingText="Pedindo a coleta"
                        flex={{ base: 1, sm: 'initial' }} minH="40px" isDisabled={expirada}>
                        {prioridade ? 'Pedir com prioridade' : 'Confirmar e pagar'} {brl(totalAgora)}
                    </Button>
                </HStack>
            );
        }
        if (etapa === 'criada' && !confirmarNaLalamove) {
            return (
                <Flex w="100%" justify="space-between" gap={2} wrap="wrap" align="center">
                    <HStack spacing={2} wrap="wrap">
                        {ordem?.cancelavel && (
                            <Button size="sm" colorScheme="red" variant="outline" leftIcon={<Icon as={FaTimesCircle} />} onClick={() => setAcao({ tipo: 'cancelar' })}>
                                Cancelar corrida
                            </Button>
                        )}
                        {ordem?.pode_trocar_motorista && (
                            <Button size="sm" colorScheme="orange" variant="outline" leftIcon={<Icon as={FaMotorcycle} />} onClick={() => setAcao({ tipo: 'trocar' })}>
                                Trocar motorista
                            </Button>
                        )}
                    </HStack>
                    <Button size="sm" onClick={onClose}>Fechar</Button>
                </Flex>
            );
        }
        if (etapa === 'escolherPonto' || etapa === 'semEndereco') return null;
        return <Button size="sm" onClick={onClose}>Fechar</Button>;
    };

    const rodape = renderRodape();

    const textoAcao = {
        cancelar: {
            title: 'Cancelar a corrida de coleta?',
            body: 'A Lalamove só devolve o valor antes de o motorista aceitar ou nos 5 primeiros minutos depois do aceite. Os pedidos voltam a ficar livres para outra corrida.',
            confirmText: 'Cancelar corrida', confirmColor: 'red',
        },
        desvincular: {
            title: `Tirar ${acao?.alvo?.rotulo || 'o pedido'} desta corrida?`,
            body: 'O pedido deixa de aparecer nesta corrida e fica livre para outra. O custo da corrida continua registrado. Se ele já foi para Retirado, isso não é desfeito: desfaça na tela da compra, se precisar.',
            confirmText: 'Tirar da corrida', confirmColor: 'red',
        },
        descartar: {
            title: 'Descartar a solicitação sem confirmação?',
            body: 'Só faça isso depois de conferir no painel da Lalamove que a corrida NÃO foi criada. Os pedidos voltam a ficar livres para outra corrida.',
            confirmText: 'Conferi, descartar', confirmColor: 'orange',
        },
        trocar: {
            title: 'Trocar o motorista?',
            body: (
                <VStack align="stretch" spacing={3}>
                    <Text fontSize="sm">O motorista atual será dispensado e a Lalamove procura outro. A corrida continua paga e o preço não muda.</Text>
                    <FormControl>
                        <FormLabel fontSize="sm" fontWeight="bold" mb={1}>Motivo</FormLabel>
                        <Select size="sm" value={motivoTroca} onChange={(e) => setMotivoTroca(e.target.value)}>
                            {Object.entries(ordem?.motivos_troca_motorista || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                        </Select>
                    </FormControl>
                    <Text fontSize="xs" color="gray.500">A Lalamove só aceita a troca depois de 15 minutos procurando motorista e antes da coleta.</Text>
                </VStack>
            ),
            confirmText: 'Dispensar e procurar outro', confirmColor: 'orange',
        },
    }[acao?.tipo] || {};

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size={compacto ? 'full' : '2xl'} scrollBehavior="inside" closeOnOverlayClick={!confirmando}>
                <ModalOverlay />
                <ModalContent>
                    {cabecalho}
                    <ModalCloseButton isDisabled={confirmando} />
                    <ModalBody py={4} ref={corpoRef}>
                        {etapa === 'revisao' && saldoInsuficiente === false && prep?.carteira?.saldo != null && prep.carteira.saldo < 50 && (
                            <Alert status="warning" borderRadius="md" fontSize="xs" mb={3}>
                                <AlertIcon /><Icon as={FaWallet} mr={1} />Saldo da carteira Lalamove baixo: {brl(prep.carteira.saldo)}.
                            </Alert>
                        )}
                        {renderCorpo()}
                    </ModalBody>
                    {rodape && <ModalFooter borderTopWidth="1px" py={3}>{rodape}</ModalFooter>}
                </ModalContent>
            </Modal>

            <ConfirmDialog
                isOpen={!!acao}
                onClose={() => setAcao(null)}
                onConfirm={executarAcao}
                isLoading={executando}
                title={textoAcao.title}
                body={textoAcao.body}
                confirmText={textoAcao.confirmText}
                cancelText="Voltar"
                confirmColor={textoAcao.confirmColor}
                wrapButtons
            />
        </>
    );
}
